import React, { useEffect, useState } from "react";
import { TableEmpty } from "@finance/components/DesignSystem";
import { api, fmtNum } from "@finance/lib/api";
import Layout, { PageHeader } from "@finance/components/Layout";
import { Input } from "@finance/components/ui/input";
import { Bank, Wallet, Money, Coins } from "@phosphor-icons/react";

export default function CashPosition() {
  const [data, setData] = useState({ banks: [], total_tzs: 0, has_missing_rate: false });
  const [fx, setFx] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/bank-accounts/cash-position").then((r) => setData(r.data)).finally(() => setLoading(false));
  const saveActual = (b, v) => {
    if (String(v) === String(b.actual_balance ?? "")) return;
    api.put(`/bank-accounts/${b.id}/actual-balance`, { actual_balance: v === "" ? null : Number(v) }).then(load);
  };
  useEffect(() => {
    load();
    api.get("/fx/exposure").then((r) => setFx(r.data)).catch(() => {});
  }, []);

  const banks = data.banks || [];
  const nonZero = banks.filter((b) => Math.abs(b.book_balance || 0) > 0.005);

  return (
    <Layout>
      <PageHeader title="Cash Position" subtitle="Book balance of every bank & cash account in one view" />
      <div className="p-8" data-testid="cash-position-page">
        <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
          <div className="soft-card p-5" data-testid="cash-total-card">
            <div className="lx-label flex items-center gap-2 text-gray-400"><Wallet size={16} />Total (TZS equiv.)</div>
            <div className={`mt-2 kpi-value text-3xl ${data.total_tzs < 0 ? "!text-red-600" : ""}`}>TSh {fmtNum(data.total_tzs || 0)}</div>
            {data.has_missing_rate && <div className="mt-1 text-xs text-amber-600">Some currencies have no FX rate — totals exclude them.</div>}
          </div>
          <div className="soft-card p-5" data-testid="cash-onhand-card">
            <div className="lx-label flex items-center gap-2 text-[#1B4383]"><Money size={16} />Cash on Hand (TZS equiv.)</div>
            <div className={`mt-2 kpi-value text-3xl ${(data.cash_total_tzs || 0) < 0 ? "!text-red-600" : "!text-[#1B4383]"}`}>TSh {fmtNum(data.cash_total_tzs || 0)}</div>
          </div>
          <div className="soft-card p-5" data-testid="cash-inbank-card">
            <div className="lx-label flex items-center gap-2 text-gray-400"><Bank size={16} />In Banks (TZS equiv.)</div>
            <div className="mt-2 kpi-value text-3xl">TSh {fmtNum(data.bank_total_tzs || 0)}</div>
          </div>
          <div className="soft-card p-5" data-testid="cash-fx-unrealized-card">
            <div className="lx-label flex items-center gap-2 text-gray-400"><Coins size={16} />Unrealized FX (TZS)</div>
            <div className={`mt-2 kpi-value text-3xl ${(fx?.unrealized_net || 0) < 0 ? "!text-red-600" : (fx?.unrealized_net || 0) > 0 ? "!text-emerald-700" : ""}`}>TSh {fmtNum(fx?.unrealized_net || 0)}</div>
            <div className="mt-1 text-xs text-gray-400">Realized to date: TSh {fmtNum(fx?.realized_net || 0)}</div>
          </div>
          <div className="soft-card p-5">
            <div className="lx-label text-gray-400">Accounts with balance</div>
            <div className="mt-2 kpi-value text-3xl">{nonZero.length}</div>
          </div>
        </div>

        {fx?.by_currency?.length > 0 && (
          <div className="mb-6 flex flex-wrap items-center gap-2" data-testid="cash-fx-note">
            <span className="lx-label text-gray-400">FX exposure @ today's spot:</span>
            {fx.by_currency.map((c) => (
              <span key={c.currency} className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-2.5 py-1 text-xs">
                <b>{c.currency}</b> {fmtNum(c.foreign_net)} <span className="text-gray-400">→ TSh {fmtNum(c.market_tzs)}</span>
                <span className={c.unrealized >= 0 ? "text-emerald-600" : "text-red-600"}>({fmtNum(c.unrealized)})</span>
              </span>
            ))}
          </div>
        )}

        <div className="overflow-hidden rounded-sm border border-gray-200 bg-white">
          <table className="w-full text-sm">
            <thead className="bg-gray-50"><tr className="lx-label border-b border-gray-200">
              <th className="px-4 py-2 text-left">Bank / Account</th>
              <th className="px-4 py-2 text-left">GL</th>
              <th className="px-4 py-2 text-left">Currency</th>
              <th className="px-4 py-2 text-right">Book Balance</th>
              <th className="px-4 py-2 text-right">FX → TZS</th>
              <th className="px-4 py-2 text-right">TZS Equivalent</th>
              <th className="px-4 py-2 text-right">Actual (memo)</th>
              <th className="px-4 py-2 text-right">Variance</th>
            </tr></thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>}
              {!loading && banks.length === 0 && <TableEmpty colSpan={8} title="No bank accounts yet" message="Add one in Bank Reconciliation to track your cash position." />}
              {banks.map((b) => (
                <tr key={b.id} data-testid={`cash-row-${b.gl_account_code}`} className={`border-b border-gray-100 hover:bg-gray-50 ${b.is_cash ? "bg-[#1B4383]/[0.03]" : ""}`}>
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2.5">
                      <span className={`grid h-6 w-6 shrink-0 place-items-center rounded-full ${b.is_cash ? "bg-[#1B4383]/10 text-[#1B4383]" : "bg-gray-100 text-gray-500"}`} title={b.is_cash ? "Cash on hand" : "Bank account"}>
                        {b.is_cash ? <Money size={13} /> : <Bank size={13} />}
                      </span>
                      <div>
                        <div className="font-medium">{b.bank_name}</div>
                        {b.account_number && <div className="text-xs text-gray-400">{b.account_number}</div>}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2 font-mono-num text-gray-500">{b.gl_account_code}</td>
                  <td className="px-4 py-2">{b.currency}</td>
                  <td className={`px-4 py-2 text-right lx-num font-semibold ${(b.book_balance || 0) < 0 ? "text-red-600" : "text-emerald-700"}`}>{fmtNum(b.book_balance || 0, b.currency)}</td>
                  <td className="px-4 py-2 text-right lx-num text-gray-500">{b.currency === "TZS" ? "—" : (b.fx_rate != null ? fmtNum(b.fx_rate) : "no rate")}</td>
                  <td className={`px-4 py-2 text-right lx-num ${(b.tzs_equivalent || 0) < 0 ? "text-red-600" : "text-gray-800"}`}>{b.tzs_equivalent != null ? fmtNum(b.tzs_equivalent, "TZS") : "—"}</td>
                  <td className="px-4 py-1 text-right"><Input type="number" className="ml-auto h-8 w-36 text-right" data-testid={`cash-actual-${b.gl_account_code}`} placeholder={b.currency}
                    defaultValue={b.actual_balance ?? ""} onBlur={(e) => saveActual(b, e.target.value)} /></td>
                  <td className={`px-4 py-2 text-right lx-num ${b.actual_balance != null && Math.abs(b.actual_balance - (b.book_balance || 0)) > 0.005 ? "font-semibold text-amber-700" : "text-gray-400"}`} data-testid={`cash-variance-${b.gl_account_code}`}>
                    {b.actual_balance != null ? fmtNum(b.actual_balance - (b.book_balance || 0), b.currency) : "—"}</td>
                </tr>
              ))}
            </tbody>
            {banks.length > 0 && (
              <tfoot>
                <tr className="border-t-2 border-gray-200 bg-gray-50 font-semibold">
                  <td className="px-4 py-2" colSpan={5}>Total (TZS equivalent)</td>
                  <td className={`px-4 py-2 text-right lx-num ${data.total_tzs < 0 ? "text-red-600" : "text-emerald-700"}`} data-testid="cash-total-footer">TSh {fmtNum(data.total_tzs || 0)}</td>
                  <td colSpan={2} className="px-4 py-2 text-right text-xs font-normal text-gray-400">Actual is memo-only — never posts</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>
    </Layout>
  );
}
